<template>
  <div
    class="dashboard-date-filters inline-flex flex-wrap items-center gap-3"
    role="group"
  >
    <div class="dashboard-segmented-control" aria-label="Période prédéfinie">
      <button
        v-for="range in quickRanges"
        :key="range.id"
        type="button"
        :class="{ 'is-active': activeRangeId === range.id }"
        @click="$emit('select-range', range)"
      >
        {{ range.label }}
      </button>
      <button
        type="button"
        :class="{ 'is-active': activeRangeId === 'full' }"
        @click="$emit('show-full')"
      >
        Tout
      </button>
    </div>

    <div class="dashboard-date-filters__field">
      <t-datepicker
        :value="rangePickerValue"
        range
        value-format="yyyy-MM-dd"
        :user-format="userFormat"
        :max-date="maxDate"
        :week-start="weekStart"
        :placeholder="placeholder"
        :variant="datepickerVariant"
        calendar-class="dashboard-datepicker-calendar"
        @input="handleRangeInput"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardDateFilters',
  props: {
    quickRanges: {
      type: Array,
      default: () => []
    },
    activeRangeId: {
      type: String,
      default: null
    },
    dateRange: {
      type: [Array, String],
      default: () => []
    },
    maxDate: {
      type: String,
      required: true
    },
    userFormat: {
      type: String,
      default: 'd/m/Y'
    },
    weekStart: {
      type: Number,
      default: 1
    },
    placeholder: {
      type: String,
      default: 'Période personnalisée'
    },
    datepickerVariant: {
      type: String,
      default: 'dashboard'
    }
  },
  emits: ['select-range', 'show-full', 'update:date-range'],
  data () {
    return {
      startLocal: null,
      endLocal: null
    }
  },
  computed: {
    rangePickerValue () {
      const start = this.startLocal ? this.toDate(this.startLocal) : null
      const end = this.endLocal ? this.toDate(this.endLocal) : null
      if (start && end) {
        return [start, end]
      }
      if (start) {
        return [start]
      }
      return []
    }
  },
  watch: {
    dateRange: {
      immediate: true,
      handler (value) {
        const [start, end] = this.normalizeRange(value)
        this.startLocal = start
        this.endLocal = end
      }
    }
  },
  methods: {
    handleRangeInput (value) {
      // In range mode the datepicker emits an array with one entry after the
      // first click and two entries once the range is complete.
      const [start, end] = this.normalizeRange(value)
      this.startLocal = start
      this.endLocal = end

      this.emitRangeIfComplete()
    },
    emitRangeIfComplete () {
      if (this.startLocal && this.endLocal) {
        let range = [this.startLocal, this.endLocal]
        if (range[0] > range[1]) {
          range = [range[1], range[0]]
          this.startLocal = range[0]
          this.endLocal = range[1]
        }

        const current = this.normalizeRange(this.dateRange)
        if (this.hasRangeChanged(range, current)) {
          this.$emit('update:date-range', range)
        }
      }
    },
    normalizeRange (value) {
      if (!value) {
        return [null, null]
      }

      let start = null
      let end = null

      if (Array.isArray(value)) {
        start = this.normalizeSingle(value[0])
        end = this.normalizeSingle(value[1])
      } else if (typeof value === 'string') {
        const parts = value.split(',').map(part => part.trim())
        start = this.normalizeSingle(parts[0])
        end = this.normalizeSingle(parts[1])
      } else if (value && typeof value === 'object') {
        start = this.normalizeSingle(value.start)
        end = this.normalizeSingle(value.end)
      }

      if (start && end && start > end) {
        return [end, start]
      }

      return [start, end]
    },
    normalizeSingle (value) {
      if (!value) {
        return null
      }
      if (Array.isArray(value)) {
        return this.normalizeSingle(value[0])
      }
      if (value instanceof Date) {
        return this.toIsoDate(value)
      }
      if (typeof value === 'string') {
        if (!value.length) {
          return null
        }
        const isoLike = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (isoLike) {
          return value
        }
        const parsed = this.toDate(value)
        return parsed ? this.toIsoDate(parsed) : null
      }
      if (value && typeof value === 'object') {
        if ('start' in value) {
          return this.normalizeSingle(value.start)
        }
        if ('end' in value) {
          return this.normalizeSingle(value.end)
        }
      }
      return null
    },
    toIsoDate (date) {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return null
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    toDate (value) {
      if (!value) {
        return null
      }
      const parts = value.split('-').map(Number)
      if (parts.length === 3 && !parts.some(Number.isNaN)) {
        const [year, month, day] = parts
        const date = new Date(year, month - 1, day)
        return isNaN(date.getTime()) ? null : date
      }
      const parsed = new Date(value)
      return isNaN(parsed.getTime()) ? null : parsed
    },
    hasRangeChanged (nextRange, currentRange) {
      const [nextStart, nextEnd] = nextRange
      const [currStart, currEnd] = currentRange
      return nextStart !== currStart || nextEnd !== currEnd
    }
  }
}
</script>

<style scoped>
.dashboard-date-filters {
  max-width: 100%;
}

.dashboard-date-filters__field {
  min-width: 16rem;
}

.dashboard-date-filters__field :deep(input) {
  color: #4b5563;
  background-color: #ffffff;
  border-color: #bfdbfe;
}

.dashboard-date-filters__field :deep(input::placeholder) {
  color: #9ca3af;
}

.dashboard-date-filters__field :deep(.t-input-group__label) {
  display: none;
}

@media (max-width: 640px) {
  .dashboard-date-filters,
  .dashboard-date-filters__field {
    width: 100%;
    min-width: 0;
  }
}
</style>
